import { Injectable } from '@angular/core';
import { FeatureStateService } from '../../shared/services/featureStateService';
import{
  CompanyModel,
  GridResponseBaseOfCompanyModel,
  NonUserModel,
  RoleCompanyModel,
  Screens,
  ScreenUser,
  UserModel,
  UserRoleCompany,
  UserRoleMappingRequest,
  UserRoleModel,
  ADUserModel,
  FilterRequest,
  ResponseModel,
  RoleModel
} from 'src/app/core/models/iam-dtos';
import { Subject, catchError, forkJoin, map, Observable, of, switchMap, tap, concat } from 'rxjs';
import { DeleteRolesTransform } from '../transforms/delete-roles.transform';
import { GetUsersByRoleIdsTransform } from '../transforms/get-users-by-roleids.transform';
import { GetRolesTransform } from '../transforms/get-roles.transform';
import { GetRoleByIdTransform } from '../transforms/get-role-by-id.transform';
import { IamWrapperConstants } from '../iamWrapperConstants';
import { AppInsightsService } from './app-insights.service';
import { CreateRoleTransform } from '../transforms/create-role.transform';
import { GetRoleNameToCopyTransform } from '../transforms/get-rolename-to-copy.transform';
import { CompanyDto, ResourcesResponseDto, HierarchicalPermissionDto, UserGroupAssignmentModel, UserResponseModel,
  IamApiService, UserWithRolesDtoGridResponseBase } from './iamapi/iamapi.services';
import { AddUsersRoleApiRequest, AddUsersRoleTransform } from '../transforms/add-users-role.transform';
import { DeleteUserRolesTransform } from '../transforms/delete-user-role.transform';
import { UpsertRoleTransform } from '../transforms/update-role.transform';
import { AppConstants } from '../appConstant';
import { GetNonRegisteredUsersTransform } from '../transforms/get-nonusers-by-role.transform';
import { GetAllUsersWithNoRoleTransform } from '../transforms/get-all-users-with-no-role.transform';
import { GetUserPermissionsTransforms } from '../transforms/get-user-permissions.transform';
import {
  GetUserAccessedCompaniesAndScreenPermissionsTransform
} from '../transforms/get-user-accessed-companies-screen-permissions.transform';
import { GetAllUsersTransform } from '../transforms/get-all-users.transform';
import { GetUserRolesTransform } from '../transforms/get-user-roles.transform';
import { UpdateRolesByUserIdTransform } from '../transforms/update-roles-by-userid.transform';
import { GetUserAccessedCompaniesTransforms } from '../transforms/get-user-accessed-companies.transform';
import { GetOtherAccessedCompaniesTransforms } from '../transforms/get-other-accessed-companies.transform';
import { MsalService } from '@azure/msal-angular';
import { AddLatestAccessedCompanyTransform } from '../transforms/add-latest-accessed-company.transform';
import { UpsertUserV2Transform } from '../transforms/upsert-usersV2.transform';
import { ScreenDisableConstants } from '../screenDisableConstants';
import { GetUserGroupsTransform } from '../transforms/get-user-groups.transform';
import { UserGroupDto } from 'src/app/core/models/user-group-model';
import { CommonRbacEvaluatorLibService } from '@cdcn/common-rbac-evaluator-lib';

@Injectable({
  providedIn: 'root',
})
export class IamWrapperService {
  private allCompanies?: CompanyDto[];
  private allResources?: ResourcesResponseDto[];
  private userAccessedCompany?: CompanyModel[];
  private userData?: UserResponseModel;
  private companyChangeInProgress = false;
  private areScreenPermissionsMissing = false;
  private readonly populateScreenPermissions = new Subject<Screens[]>();

  constructor(
    private readonly iamApiService: IamApiService,
    private readonly featureState: FeatureStateService,
    private readonly appInsightsService: AppInsightsService,
    private readonly msalService: MsalService,
    private readonly commonRbacEvaluatorService: CommonRbacEvaluatorLibService,
  ) {}

  get PopulateScreenPermissions() {
    return this.populateScreenPermissions.asObservable();
  }

  get UseIamApis() {
    return this.featureState.useIamApis();
  }
  get UserObjectId() {
    return this.userData?.objectId ?? '';
  }

  get UserAccessedCompany(): CompanyModel[] {
    return this.userAccessedCompany;
  }

  set UserAccessedCompany(value: CompanyModel[]) {
    this.userAccessedCompany = value;
  }

  get CompanyChangeInProgress() {
    return this.companyChangeInProgress;
  }

  set CompanyChangeInProgress(value: boolean) {
    this.companyChangeInProgress = value;
  }

  get AreScreenPermissionsMissing() {
    return this.areScreenPermissionsMissing;
  }

  set AreScreenPermissionsMissing(value: boolean) {
    this.areScreenPermissionsMissing = value;
  }

  getInitialData() {
      const getCompanies$ = this.getCompanies();
      const getResources$ = this.getResources();
      return forkJoin([getCompanies$, getResources$]);
  }

  getCompanies() {
    return this.iamApiService.getCompanies().pipe(
      tap(companies => (this.allCompanies = companies)),
      catchError(error => {
        this.appInsightsService.trackTrace(IamWrapperConstants.CompanyDataFetchError, error);
        return of([]);
      }),
    );
  }

  getResources() {
    return this.iamApiService.getResources(IamWrapperConstants.U2ApplicationName.toLowerCase()).pipe(
      tap(resources => (this.allResources = resources)),
      catchError(error => {
        this.appInsightsService.trackTrace(IamWrapperConstants.ResourcesDataFetchError, error);
        return of([]);
      }),
    );
  }

  getUserAccessedCompaniesAndScreenPermissions(): Observable<{
    screenPermissions: ScreenUser;
    companyData: CompanyModel[];
  }> {
      return this.getUserDataWithPermissions().pipe(GetUserAccessedCompaniesAndScreenPermissionsTransform.transformOutput);
  }

  getUserPermissions(): Observable<ScreenUser> {
      let userDataWithPermissions$: Observable<UserResponseModel>;
      if ((this.userData === null || this.userData === undefined) || this.CompanyChangeInProgress) {
        userDataWithPermissions$ = this.getUserDataWithPermissions();
      } else {
        userDataWithPermissions$ = of(this.userData);
      }
      return userDataWithPermissions$.pipe(GetUserPermissionsTransforms.transformOutput);
  }

  getUserAccessedCompanies(): Observable<CompanyModel[]> {
      let userDataWithPermissions$: Observable<UserResponseModel>;
      if ((this.userData === null || this.userData === undefined) || this.CompanyChangeInProgress) {
        userDataWithPermissions$ = this.getUserDataWithPermissions();
      } else {
        userDataWithPermissions$ = of(this.userData);
      }
      return userDataWithPermissions$.pipe(GetUserAccessedCompaniesTransforms.transformOutput);
  }

  getOtherAccessedCompanies(
    sortColumn?: string | undefined,
    sortDirection?: string | undefined,
    filterColumn?: string | undefined,
    filterValue?: string | undefined,
    pageSize?: number | undefined,
    pageNumber?: number | undefined,
    existingCompanies?: string | undefined,
  ): Observable<GridResponseBaseOfCompanyModel> {
      let userDataWithPermissions$: Observable<UserResponseModel>;
      if ((this.userData === null || this.userData === undefined) || this.CompanyChangeInProgress) {
        userDataWithPermissions$ = this.getUserDataWithPermissions();
      } else {
        userDataWithPermissions$ = of(this.userData);
      }
      return GetOtherAccessedCompaniesTransforms.transformOutput(userDataWithPermissions$, pageSize, pageNumber, filterColumn, filterValue);
  }

  addLatestAccessedCompany(userRoleCompany: UserRoleCompany): Observable<CompanyModel[]> {
      const userObjectId = this.UserObjectId;
      const requestBody = AddLatestAccessedCompanyTransform.transformInput(userRoleCompany, userObjectId, this.userData.companies);
      return this.iamApiService.audit(requestBody).pipe(
        switchMap(_ =>
          this.iamApiService.getUser(IamWrapperConstants.U2ApplicationName.toLowerCase()
            ).pipe(
            tap(newUserData => {
              // Update only the user companies with the new set of companies
              // UserData has screenpermissions which is not included in this API call
              // Hence, a full copy of the user data is NOT to be done
              if (this.userData !== null && this.userData !== undefined) {
                this.userData.companies = newUserData.companies;
              }
            }),
          ),
        ),
        GetUserAccessedCompaniesTransforms.transformOutput,
      );
  }

  getAllUsersWithNoRole(): Observable<UserModel[]> {
    const filterRequest = new FilterRequest({
      pageSize: IamWrapperConstants.MaxPageSize,
      pageNumber: IamWrapperConstants.DefaultPageNumber,
    });
      return this.iamApiService
        .getAllUsers(IamWrapperConstants.U2ApplicationName.toLowerCase(), false, filterRequest)
        .pipe(GetAllUsersWithNoRoleTransform.transformOutput);
  }

  getRoleNameToCopy(roleId: number): Observable<string> {

      return this.iamApiService
        .getRoles(IamWrapperConstants.U2ApplicationName.toLowerCase())
        .pipe(observableResponse => GetRoleNameToCopyTransform.transformOutput(observableResponse, roleId));

  }

  getRole(roleId: string): Observable<RoleModel> {
      const roleIdNumber = GetRoleByIdTransform.transformInput(roleId);
      return this.iamApiService
        .getRoleById(roleIdNumber)
        .pipe(observableResponse => GetRoleByIdTransform.transformOutput(observableResponse, this.allCompanies, this.allResources));

  }

  getFilteredRoles(): Observable<RoleModel[]> {
      return this.iamApiService
        .getRoles(IamWrapperConstants.U2ApplicationName.toLowerCase())
        .pipe(observableResponse =>
          GetRolesTransform.transformFilteredOutput(observableResponse, this.allCompanies, AppConstants.CurrentCompanyCode),
        );

  }

  getAllRoles(): Observable<RoleCompanyModel[]> {
      return this.iamApiService
        .getRoles(IamWrapperConstants.U2ApplicationName.toLowerCase())
        .pipe(observableResponse => GetRolesTransform.transformOutput(observableResponse, this.allCompanies));

  }

  getAllUsers(applicationName: string = null, filterRequest: FilterRequest = null): Observable<UserModel[]> {
      if (!filterRequest) {
        filterRequest = new FilterRequest({
          pageSize: IamWrapperConstants.PerfOptimizedLargePageSize,
          pageNumber: IamWrapperConstants.DefaultPageNumber,
        });
      }
      applicationName = applicationName || IamWrapperConstants.U2ApplicationName;
      return this.iamApiService
        .getAllUsers(applicationName.toLowerCase(), false, filterRequest)
        .pipe(GetAllUsersTransform.transformOutput);
  }

  getAllUsersGridResponseBase(applicationName: string = null, filterRequest: FilterRequest = null):
    Observable<UserWithRolesDtoGridResponseBase> {
      if (!filterRequest) {
        filterRequest = new FilterRequest({
          pageSize: IamWrapperConstants.PerfOptimizedLargePageSize,
          pageNumber: IamWrapperConstants.DefaultPageNumber,
        });
      }
      applicationName = applicationName || IamWrapperConstants.U2ApplicationName;
      return this.iamApiService.getUsersList(applicationName.toLowerCase(), false, filterRequest);
  }

  getAvilableAssignedRolesByUserId(selectedUser: UserModel): Observable<any> {
      const allRoles$ = this.iamApiService.getRoles(IamWrapperConstants.U2ApplicationName.toLowerCase());
      const userRoles$ = this.iamApiService.getUserRoles(selectedUser[IamWrapperConstants.UserObjectIdKey]);
      return forkJoin([allRoles$, userRoles$]).pipe(
        map(([allRoles, userRoles]) => GetUserRolesTransform.transformOutput(allRoles, userRoles)),
      );
  }

  getAvailableAndAssignedGroupsByUserId(selectedUser: UserModel): Observable<UserGroupDto> {
      const allGroups$ = this.iamApiService.getGroups(IamWrapperConstants.UserGroupType);
      const userGroups$ = this.iamApiService.getUserGroupsByUserId(selectedUser[IamWrapperConstants.UserObjectIdKey]);
      return forkJoin([allGroups$, userGroups$]).pipe(
        map(([allGroups, userGroups]) => GetUserGroupsTransform.transformOutput(allGroups, userGroups)),
      );
  }

  updateAssignedGroupsByUserId(
    selectedUser: UserModel,
    assignedGroups: UserGroupAssignmentModel[],
    unassignedGroups: string[]
  ): Observable<ResponseModel> {
      const assignGroupsResponse$ = assignedGroups.length > 0
        ? this.iamApiService.assignUserGroups(selectedUser[IamWrapperConstants.UserObjectIdKey], assignedGroups)
        : of(null);
      const unassignGroupsResponse$ = unassignedGroups.length > 0
        ? this.iamApiService.unassignUserGroups(selectedUser[IamWrapperConstants.UserObjectIdKey], unassignedGroups)
        : of(null);

      return concat(assignGroupsResponse$, unassignGroupsResponse$).pipe(
        map(() => {
          const isSuccess = true;
          const message = IamWrapperConstants.GroupAssignSuccess;
          return new ResponseModel({ isSuccess, message });
        })
      );
  }

  checkUsersByRoleIds(roleIds?: string | undefined): Observable<any> {
      // multiple api calls are needed
      const filterRequest: FilterRequest = new FilterRequest({
        pageSize: IamWrapperConstants.DefaultPageSize,
        pageNumber: IamWrapperConstants.DefaultPageNumber,
      });
      const roleIdsDto = GetUsersByRoleIdsTransform.transformInput(roleIds);

      // Create an array of observables for each roleId
      const observables = roleIdsDto.map(roleId => this.iamApiService.getUsersByRole(roleId, filterRequest));

      // Use forkJoin to wait for all observables to complete
      return forkJoin(observables).pipe(
        map(responses => {
          // Check if any response has users
          const hasUsers = responses.some(response => response.length > 0);
          return { hasUsers };
        }),
      );
  }

  deleteRole(roleIds?: string | undefined): Observable<ResponseModel> {
      const roleIdsDto = DeleteRolesTransform.transformInput(roleIds);
      return this.iamApiService.deleteRole(roleIdsDto).pipe(DeleteRolesTransform.transformOutput);
  }

  clearUserCache(): Observable<any> {
      return this.iamApiService.clearCache();
  }

  createRole(): Observable<RoleModel> {
      return this.iamApiService
        .getResources(IamWrapperConstants.U2ApplicationName.toLowerCase())
        .pipe(observableResponse => CreateRoleTransform.transformOutput(observableResponse, this.allCompanies));
  }

  addRole(roleModel: RoleModel): Observable<ResponseModel> {
      const addRoleInputDto = UpsertRoleTransform.transformInput(roleModel);
      return this.iamApiService.upsertRole(addRoleInputDto).pipe(UpsertRoleTransform.transformOutput);
  }

  updateRole(roleModel: RoleModel): Observable<ResponseModel> {
      const updateRoleInputDto = UpsertRoleTransform.transformInput(roleModel);
      return this.iamApiService.upsertRole(updateRoleInputDto).pipe(UpsertRoleTransform.transformOutput);
  }

  getUsersByRoleId(roleId: any): Observable<UserModel[]> {
      const filterRequest: FilterRequest = new FilterRequest({
        pageSize: IamWrapperConstants.MaxPageSize,
        pageNumber: IamWrapperConstants.DefaultPageNumber,
      });
      return this.iamApiService.getUsersByRole(roleId, filterRequest).pipe(GetUsersByRoleIdsTransform.transformOutput);
  }

  addUserRole(userRoleModel: UserRoleModel): Observable<ResponseModel> {
      const addUsersRoleApiRequests: AddUsersRoleApiRequest[] = AddUsersRoleTransform.transformInput(userRoleModel);

      const observables = addUsersRoleApiRequests.map(addUserRoleRequest =>
        this.iamApiService.assignRolesToUsers(addUserRoleRequest.userId, addUserRoleRequest.body),
      );

      return forkJoin(observables).pipe(
        map(() => {
          const isSuccess = true;
          const message = IamWrapperConstants.UserAssignSuccess;
          return new ResponseModel({ isSuccess, message });
        }),
      );
  }

  deleteUserRole(userIds: string[], roleId: string, userRoleIds?: string | undefined): Observable<ResponseModel> {
      const unassignUsersForRole = DeleteUserRolesTransform.transformInput(userIds, roleId);

      const observables = unassignUsersForRole.map(unassignUserRoleRequest =>
        this.iamApiService.unassignUserRole(unassignUserRoleRequest.userId, unassignUserRoleRequest.body),
      );

      return forkJoin(observables).pipe(
        map(() => {
          const isSuccess = true;
          const message = IamWrapperConstants.UserUnassignSuccess;
          return new ResponseModel({ isSuccess, message });
        }),
      );
  }

  getNonUsersByRoleId(roleId: any): Observable<NonUserModel[]> {
      const filterRequest: FilterRequest = new FilterRequest({
        filterColumn: IamWrapperConstants.EmptyString,
        filterValue: IamWrapperConstants.EmptyString,
        pageSize: IamWrapperConstants.MaxPageSize,
        pageNumber: IamWrapperConstants.DefaultPageNumber,
      });
      return this.iamApiService
        .getUnassignedUsersForRole(roleId, IamWrapperConstants.U2ApplicationName.toLowerCase(), filterRequest)
        .pipe(GetNonRegisteredUsersTransform.transformOutput);
  }

  updateAssignedRolesByUserId(userRoleMappingRequest: UserRoleMappingRequest): Observable<ResponseModel> {
      const [assignRolesRequest, unassignRolesRequest] = UpdateRolesByUserIdTransform.transformInput(userRoleMappingRequest);
      const assignRolesResponse$ = this.iamApiService.assignRolesToUsers(assignRolesRequest.userId, assignRolesRequest.requestBody);
      const unassignRolesResponse$ = this.iamApiService.unassignUserRole(unassignRolesRequest.userId, unassignRolesRequest.requestBody);
      return forkJoin([assignRolesResponse$, unassignRolesResponse$]).pipe(
        map(() => {
          const isSuccess = true;
          const message = IamWrapperConstants.RoleAssignSuccess;
          return new ResponseModel({ isSuccess, message });
        }),
      );
  }

  createADUsers(selectedUserData: ADUserModel[]): Observable<string> {
      const upsertUserV2Input = UpsertUserV2Transform.transformInput(selectedUserData);
      const observables = upsertUserV2Input.map(upsertUserV2Request => this.iamApiService.upsertUserV2(upsertUserV2Request));

      return forkJoin(observables).pipe(UpsertUserV2Transform.transformOutput);
  }

  public getUserAssignedHierarchicalPermissions(): Observable<HierarchicalPermissionDto> {
    if(this.featureState.isU2UseHierarchicalPermissionEnabled()) {
      const userObjectId = this.UserObjectId;
      return this.iamApiService.getUserAssignedHierarchicalPermissions(userObjectId, sessionStorage.getItem('CompanyCode').toString());
    }
    else {
      return of(new HierarchicalPermissionDto(
        {
          permissions: [],
          deniedPermissions: []
        }
      ));
    }
  }

  public handleDisplayBasedOnCompanies() {
    const screens = this.getScreenPermissions();
    if (screens) {
      const codesToDisable = this.getListOfCodesToDisable();
      screens.forEach(screen => {
        if (codesToDisable.includes(screen.screenCode)) {
          screen.display = false;
        }
      });
      this.AreScreenPermissionsMissing = false;
      this.populateScreenPermissions.next(screens);
    }
    else {
      this.AreScreenPermissionsMissing = true;
    }
  }

  public getListOfCodesToDisable() {
    const listOfCodesToDisable: string[] = [];
    if (!this.featureState.isCCHEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.CHPRT);
      listOfCodesToDisable.push(ScreenDisableConstants.CHL);
    }
    if (!this.featureState.isQualificationEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.QUAL);
    }
    if (!this.featureState.isAdminTimeAdjustmentEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.ADJAT);
    }
    if (!this.featureState.isNotificationEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.NTF);
    }
    if (!this.featureState.isCaregiverDemographicsConfirmationEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.ATREV);
    }
    if (!this.featureState.isCaregiverParticipantRelationshipConfirmationEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.PRREV);
    }
    if (!this.featureState.isStatusEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.PSCFG);
    }
    if (!this.featureState.isServiceCodeUsageEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.SVCUL);
    }
    if (!this.featureState.isFeaTransferEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.FEA);
    }
    if (!this.featureState.isPayRatesInjectorEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.PYRTI);
    }
    if (!this.featureState.isUserCreationToolScreenEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.UCT);
    }
    if (!this.featureState.isU2UseHierarchicalPermissionEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.RHPFM);
    }
    if (!this.featureState.isEmployerCaregiverScreenEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.CGEMP);
    }
    if(!this.featureState.isCoEmploymentPayrollConfigurationEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.CFGPR);
    }
    if(this.featureState.isCoEmploymentPayrollConfigurationEnabled()) {
      listOfCodesToDisable.push(ScreenDisableConstants.CFTPR);
    }
    if (!this.featureState.isTraditionalPayrollWorkday()) {
      listOfCodesToDisable.push(ScreenDisableConstants.PREC);
    }
    return listOfCodesToDisable;
  }

  private getScreenPermissions() {
    try {
      const item = sessionStorage.getItem('screenpermssions');
      if (item) {
        return <Screens[]>JSON.parse(atob(item));
      }
    } catch {}
  }

  private getUserDataWithPermissions() {
    const includePermissions = true;
    const userDataWithPermissions$ = this.iamApiService
      .getUser(IamWrapperConstants.U2ApplicationName.toLowerCase(), includePermissions)
      .pipe(tap(userData => this.transformUserDataWithPermissions(userData)));
    return userDataWithPermissions$;
  }

  private transformUserDataWithPermissions(userData: UserResponseModel) {
    userData.resources = userData.resources.slice().sort((a, b) => a.displayOrder - b.displayOrder);
    this.userData = userData;
    this.setUserPermissionsForRbac(userData);
  }

  private setUserPermissionsForRbac(userData: UserResponseModel) {
    const permissions: string[] = [];
    if (userData?.resources) {
      userData.resources.forEach(resource => {
        if (resource.permissions) {
          resource.permissions.forEach(permission => {
            if (permission.permissionName && resource.code) {
              // Full permission format: "STSEC Read" (resource code + permission name)
              const fullPermissionName = `${resource.code} ${permission.permissionName}`;
              permissions.push(fullPermissionName);
            }
          });
        }
      });
    }
    this.commonRbacEvaluatorService.userPermissions = permissions;
  }
}
